/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Dispatcher } from 'src/core/types/dispatcher';
import { Handler, HandlerGroup, HandlerGroupFactoryDeps } from 'src/core/types/handler';
import { attachHandlerMiddleware } from 'src/core/utils/handlerMiddleware';
import { getLifetime } from 'src/core/utils/lifetime';

/**
 * Creates the global dispatcher that will be used by all components
 * to request data and apply mutations. A dispatcher installs
 * handler groups (which are supported units of work ex: `addIntent()`,
 * `deleteApp()`, etc.) to its internal map and exposes them via the
 * dispatch method.
 */
export const createDispatcher = <T extends HandlerGroup>(dependencies: HandlerGroupFactoryDeps): Dispatcher<T> => {
    const handlersMap = new Map<string, Handler>();

    return {
        /**
         * Dispatches (fires) a handler with the given handler name
         * and passes the given parameters to that handler (function).
         *
         * @param handlerName The signature of the handler (function) to fire.
         * @param params The parameters to pass to the function.
         * @throws An Error if the handler name passed doesn't exist.
         */
        dispatch: (handlerName, ...params) => {
            const handlerToDispatch = handlersMap.get(handlerName);

            if (!handlerToDispatch) {
                throw new Error(`A handler with the signature '${handlerName}' is not found. Please make sure it installed.`);
            }

            // Dispatcher now allows one parameter only.
            attachHandlerMiddleware(handlerToDispatch, params[0], dependencies);
        },

        /**
         * Installs the given handler group the current internal handler
         * group the dispatcher has. Creates a disposer function to
         * remove that handler group from the dispatcher when the
         * lifetime of the handler group is over.
         *
         * @param handlerGroupFactory A factory function to create the
         * handler group.
         * @throws An error if the handler group is duplicated.
         * @returns A disposer (uninstall) function to dispose of the
         * handler group that is being installed.
         */
        installHandlerGroup: handlerGroupFactory => {
            const lifetime = getLifetime();
            const handlerGroup = handlerGroupFactory({ ...dependencies, lifetime });

            Object.keys(handlerGroup).forEach(handlerName => {
                if (handlersMap.has(handlerName)) {
                    throw new Error(`A handler with the signature '${handlerName}' is already installed.`);
                }

                handlersMap.set(handlerName, handlerGroup[handlerName]);
            });

            return () => {
                Object.keys(handlerGroup).forEach(handlerName => handlersMap.delete(handlerName));
                lifetime.dispose();
            };
        }
    };
};
